import { useApolloClient, useMutation } from '@apollo/client';
import { useNotify } from '@newfront-insurance/next-notifications';
import { useEffect, useRef } from 'react';

import { getNovaGroupError, INLINE_ERROR_MUTATION_OPTIONS } from './group-mutation-errors';
import {
  TRANSFER_SKILL_GROUP_TEAM_MANAGER,
  type SkillGroupTeamManagerTransfer,
} from '../../../../queries/skill-group-people';
import { updateCachedGroupRoles } from '../../lib/skill-group-cache';
import { getEmployeeDisplayName, type SettingsEmployee } from '../settings-people/employee-utils';
import { SettingsPeopleDialog, type SettingsPeopleSubmitResult } from '../settings-people/people-dialog';

export interface TeamManagerDialogTeam {
  uuid: string;
  name: string;
  parentGroupUuid: string;
  managers: string[];
}

/**
 * Hands a team to someone in its organization. Its manager, its organization admins, and Nova operations may do this;
 * the BFF decides, so callers can offer it without knowing the viewer's role. Mount only while open.
 */
export function TeamManagerDialog({
  team,
  currentManagerName,
  onClose,
  onChanged,
}: {
  team: TeamManagerDialogTeam;
  currentManagerName?: string;
  onClose: () => void;
  onChanged?: (group: SkillGroupTeamManagerTransfer) => void;
}) {
  const client = useApolloClient();
  const notify = useNotify();
  const [transfer] = useMutation(TRANSFER_SKILL_GROUP_TEAM_MANAGER, INLINE_ERROR_MUTATION_OPTIONS);
  const active = useRef(true);
  useEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
    };
  }, []);

  const submit = async ([employee]: SettingsEmployee[]): Promise<SettingsPeopleSubmitResult> => {
    if (!employee) return { failedUserUuids: [] };
    const fail = (error: string) => ({ failedUserUuids: [employee.userUuid], error });
    let group: SkillGroupTeamManagerTransfer | undefined;
    try {
      const { data } = await transfer({ variables: { groupId: team.uuid, userUuid: employee.userUuid } });
      group = data?.transferSkillGroupTeamManager;
    } catch (error) {
      return fail(
        getNovaGroupError(error)?.message ?? "Couldn't change the team manager. Check your access and try again.",
      );
    }
    if (
      group?.uuid !== team.uuid ||
      group.parentGroupUuid !== team.parentGroupUuid ||
      !group.managers.includes(employee.userUuid)
    ) {
      return fail('The new team manager was not confirmed. Refresh the team and try again.');
    }
    updateCachedGroupRoles(client.cache, group);
    if (active.current) notify.success(`${getEmployeeDisplayName(employee)} is now the team manager.`);
    onChanged?.(group);
    return { failedUserUuids: [] };
  };

  return (
    <SettingsPeopleDialog
      single
      title="Change team manager"
      description={
        `Choose someone from ${team.name}'s organization to manage it.` +
        (currentManagerName ? ` ${currentManagerName} will stay on the team as a member.` : '')
      }
      submitLabel="Make team manager"
      pendingLabel="Saving..."
      organizationUuid={team.parentGroupUuid}
      excludedUserUuids={new Set(team.managers)}
      excludedLabel="Team manager"
      onClose={onClose}
      onSubmit={submit}
    />
  );
}
